'use es6';

import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import { Link } from 'foundations-components/transitional/components/Link';
import { MediaQueryView } from 'marketplace-ui-common/layout/components/MediaQueryView';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import { isSucceeded } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import StorefrontResultsHeader from 'marketplace-ui-common/storefront/components/StorefrontResultsHeader';
import { getCredentialDisplayName } from 'marketplace-ui-common/utils/credentials';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import * as FilterKeys from '../../constants/search/FilterKey';
import { DEFAULT_SEARCH_LIMIT, FILTER_TAG_KEYS } from '../../constants/search/Filters';
import { getAccreditations, getAccreditationsRequestState } from '../../selectors/accreditations';
import { getStorefrontProfilesTotal } from '../../selectors/storefront';
import { getServices, getServicesRequestState } from '../../slices/serviceCatalogSlice';
import { getCredentialById } from '../../utils/credentials';
import { clearRecentActivity } from '../../utils/homepage';
import { getStorefrontResultsHeaderText } from '../../utils/i18n';
import FilterPanelNavigator from '../filters/mobile/FilterPanelNavigator';
import FindPartnerWizard from '../homepage/questionnaire/FindPartnerWizard';
import ResultsFilters from './ResultsFilters';
import SortDropdown from './SortDropdown';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const resultsHeaderTextPath = 'ecosystem-marketplace-solutions-ui.storefront.results.header';
const resultsCountTextPath = 'ecosystem-marketplace-solutions-ui.storefront.results.resultsCount';
const ResultsHeader = ({
  filters,
  onClearFilters,
  onDeleteFilter,
  onUpdateFilter,
  requestState,
  hasResults
}) => {
  const lang = getLang();
  const accreditations = useSelector(getAccreditations);
  const accreditationRequestState = useSelector(getAccreditationsRequestState);
  const catalogService = useSelector(getServices);
  const servicesRequestState = useSelector(getServicesRequestState);
  const servicesAvailable = isSucceeded(servicesRequestState);
  const total = useSelector(getStorefrontProfilesTotal);
  const filtersCount = useMemo(() => Object.keys(filters).filter(key => FILTER_TAG_KEYS.indexOf(key) !== -1 && filters[key] && filters[key].length >= 1).length, [filters]);
  const headerText = useMemo(() => {
    let text = getStorefrontResultsHeaderText(filters, catalogService);
    if (filters[FilterKeys.ACCREDITATION].length === 1 && filtersCount === 1) {
      const accreditationDisplayName = getCredentialDisplayName(getCredentialById(accreditations, filters[FilterKeys.ACCREDITATION]), lang);
      text = /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${resultsHeaderTextPath}.accreditation`,
        options: {
          accreditation: accreditationDisplayName.replace('Accreditation', '').trim()
        }
      });
    }
    return isSucceeded(accreditationRequestState) && text;
  }, [accreditationRequestState, accreditations, catalogService, filters, filtersCount, lang]);
  const location = useLocation();
  const [showFindPartnerWizard, setShowFindPartnerWizard] = useState(false);
  const redirectedFromHomepage = location.search.includes('ref=questionnaire');
  const renderedResultsCount = useMemo(() => {
    if (!isSucceeded(requestState) || !total) {
      return null;
    }
    const searchTerm = filters[FilterKeys.SEARCH];
    const options = {
      start: filters[FilterKeys.OFFSET] + 1,
      end: Math.min(total, filters[FilterKeys.OFFSET] + DEFAULT_SEARCH_LIMIT),
      total
    };
    if (filters[FilterKeys.SEARCH]) {
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `${resultsCountTextPath}.searchTerm_jsx`,
        options: Object.assign({}, options, {
          searchTerm
        }),
        elements: {
          Bold: 'b'
        }
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: `${resultsCountTextPath}.default`,
      options: options
    });
  }, [filters, requestState, total]);
  const renderedTitle = useMemo(() => {
    if (redirectedFromHomepage) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ecosystem-marketplace-solutions-ui.storefront.results.header.yourResults"
      });
    }
    return hasResults && headerText;
  }, [hasResults, headerText, redirectedFromHomepage]);
  const defaultHeader = /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(StorefrontResultsHeader, {
      title: renderedTitle,
      description: redirectedFromHomepage && /*#__PURE__*/_jsxs("span", {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystem-marketplace-solutions-ui.storefront.results.header.questionnaire"
        }), /*#__PURE__*/_jsx(Link, {
          onClick: () => {
            if (servicesAvailable) {
              clearRecentActivity();
              setShowFindPartnerWizard(true);
            }
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ecosystem-marketplace-solutions-ui.storefront.results.header.takeQuestionnaireAgain"
          })
        })]
      }),
      renderedFiltersPanel: /*#__PURE__*/_jsx(FilterPanelNavigator, {
        filters: filters,
        location: location,
        onClearFilters: onClearFilters,
        onDeleteFilter: onDeleteFilter,
        onSetFilter: onUpdateFilter
      }),
      renderedResultsCount: renderedResultsCount,
      renderedResultsFilters: /*#__PURE__*/_jsx(ResultsFilters, {
        marginBlockEnd: "400",
        filters: filters,
        onClearFilters: onClearFilters,
        onCloseFilterTag: onDeleteFilter
      }),
      renderedSortDropdown: hasResults && /*#__PURE__*/_jsx(SortDropdown, {
        filters: filters
      })
    })
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(MediaQueryView, {
      min: ScreenSizes.SM,
      children: [defaultHeader, showFindPartnerWizard && /*#__PURE__*/_jsx(FindPartnerWizard, {
        onClose: () => setShowFindPartnerWizard(false),
        isMobileView: false
      })]
    }), /*#__PURE__*/_jsxs(MediaQueryView, {
      max: ScreenSizes.SM,
      children: [/*#__PURE__*/_jsx(StorefrontResultsHeader, {
        title: headerText,
        renderedResultsCount: renderedResultsCount
      }), showFindPartnerWizard && /*#__PURE__*/_jsx(FindPartnerWizard, {
        onClose: () => setShowFindPartnerWizard(false),
        isMobileView: true
      })]
    })]
  });
};
export default ResultsHeader;